import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	IconButton,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useGetMajorListsQuery,
	useGetUserDetailQuery,
	useUnlockMajorMutation,
} from '@/api/user/userApiSlice';
import {
	User21Filter,
	UserAdvanceSeriesPageResDTO,
} from '@/api/DTO/user/userList.interface';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Series21ProgressSummaryFilterSection from './components/MajorProgressSummaryFilterSection';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import useModal from '@/common/components/ConfirmationDialog/useModal';

export interface DeleteUserListDataGrid {
	updatedAt: string;
}

export default function MajorProgressSummaryPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const { id } = useParams();
	const showModal = useModal();
	const [unlockMajor] = useUnlockMajorMutation();

	const { data: userDetail }: any = useGetUserDetailQuery(id || '', {
		refetchOnMountOrArgChange: true,
	});

	const defaultPropertyFilter: User21Filter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		statuses: [],
		id: id || '',
	};
	const defaultValues: DefaultValues<User21Filter> = defaultPropertyFilter;
	const methods = useForm<User21Filter>({
		defaultValues,
	});
	const transformedSearchParams: User21Filter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'statuses',
			'id',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMajorListsQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: UserAdvanceSeriesPageResDTO[]) => {
			if (response == null) return;
			const newResponse: DeleteUserListDataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as User21Filter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const handleUnlock = (row: UserAdvanceSeriesPageResDTO) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('user.unlockSeries')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('user.isUnlockSeries')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await unlockMajor({
						id: id || '',
						seriesId: row.seriesId,
					});
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.unlockSeriesSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('user.seriesTitleZh'),
			flex: 21,
		},
		{
			field: 'authorZh',
			headerName: t('marjorExercise.authorZh'),
			flex: 21,
		},
		{
			field: 'published',
			headerName: t('user.seriesOnStatus'),
			flex: 21,
			renderCell: (params) => (
				<Chip
					color={params.row.published ? 'chipPrimary' : 'default'}
					label={params.row.published ? t('day21.on') : t('day21.off')}
				/>
			),
		},
		{
			field: 'unlocked',
			headerName: t('user.seriesUnlockStatus'),
			flex: 21,
			renderCell: (params) =>
				typeof params.row.unlocked == 'boolean' ? (
					<Chip
						color={params.row.unlocked ? 'chipPrimary' : 'default'}
						label={params.row.unlocked ? t('user.unlock') : t('user.lock')}
					/>
				) : (
					'-'
				),
		},
		{
			field: 'count',
			headerName: t('user.userUnlockProgress'),
			flex: 21,
			renderCell: (params) =>
				`${params.row.completedCount}/${params.row.singleCount}`,
		},
		{
			field: 'updatedAt',
			headerName: t('user.progressUpdateTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				params.row.unlocked ? (
					<Link
						to={`/user-manage/user-list/info/${id}/major/${params.row.seriesId}`}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='primary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				) : (
					<IconButton color='primary'>
						<LockOpenIcon onClick={() => handleUnlock(params.row)} />
					</IconButton>
				),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('user.majorProgress')}({userDetail?.data.username},
					{userDetail?.data.nickname})
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<Series21ProgressSummaryFilterSection></Series21ProgressSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.seriesId}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
